import React, { useRef, useEffect } from "react"
import DataLoader, { factors } from "../DataLoader"

import gradient from "../utils/gradient"

import "./GlobalInsightsTable.scss"
import { globalInsightsConstants } from "./"
import { useTransition, animated } from "react-spring"

import { factorHeaderLabels } from "../Factors/"
import { useMediumWidthMode } from "../utils/smallWidthMode"
import getShortCountryLabel from "../utils/getShortCountryLabel"

export default function GlobalInsightsTable(props) {
  const {
    selectedYear,
    selectedFactor,
    containerDimensions,
    selectedFilterIncome,
    selectedFilterRegion,
    selectedSortOrder,
    selectedCountry,
    headerHeight,
  } = props
  let countries = [...DataLoader.dataByYear[selectedYear].countries]

  if (selectedFilterIncome !== "") {
    countries = countries.filter(
      (country) => country["Income Group"] === selectedFilterIncome,
    )
  }
  if (selectedFilterRegion !== "") {
    countries = countries.filter(
      (country) => country.Region === selectedFilterRegion,
    )
  }
  const inSmallWidthMode = useMediumWidthMode()
  const selectedCountryRef = useRef()
  useEffect(() => {
    if (
      selectedCountry !== "" &&
      selectedCountryRef.current &&
      props.containerRef &&
      headerHeight
    ) {
      const scrollDomNode = inSmallWidthMode
        ? document.documentElement
        : props.containerRef
      const scrollOffset =
        selectedCountryRef.current.getBoundingClientRect().y +
        scrollDomNode.scrollTop -
        220 // 220 is about the size of the combined headers
      scrollDomNode.scrollTo({
        top: scrollOffset,
        behavior: "smooth",
      })
    }
  }, [selectedCountry, props.containerRef, inSmallWidthMode, headerHeight])

  let scrollToCountryNeeded = useRef(false)
  useEffect(() => {
    scrollToCountryNeeded.current = true
  }, [selectedFactor, selectedYear])

  const factorSelected = selectedFactor !== ""
  const factorIndex = factorSelected ? factors.indexOf(selectedFactor) : -1

  const { rowHeight, labelRankPadding, sidePadding, barHeight } =
    globalInsightsConstants

  const svgHeight = countries.length * rowHeight
  const sidePaddingToUse = inSmallWidthMode ? labelRankPadding : sidePadding
  const svgWidth =
    (containerDimensions.width ? containerDimensions.width : 300) -
    sidePaddingToUse

  const sortScoreKey = factorSelected
    ? `Factor ${factorIndex + 1}: ${selectedFactor}`
    : "overall"

  countries.sort((a, b) => {
    return b[sortScoreKey] - a[sortScoreKey]
  })
  countries.forEach((country, countryIndex) => {
    country.sortedRank = countryIndex
  })

  countries.sort((a, b) => {
    if (selectedSortOrder === "alphabetical") {
      const aCountryMinusThe = a.Country.replace("The ", "")
      const bCountryMinusThe = b.Country.replace("The ", "")
      if (aCountryMinusThe > bCountryMinusThe) {
        return 1
      }
      return -1
    }
    return b[sortScoreKey] - a[sortScoreKey]
  })

  const labelWidth = Math.max(
    svgWidth * globalInsightsConstants.labelWidthPercentage,
    globalInsightsConstants.minLabelWidth,
  )
  const fullBarWidth = svgWidth - labelWidth

  const allFactors = ["rank", ...factors, "overall"]
  const factorWidth = fullBarWidth / allFactors.length
  const boxPadding = 1

  const getTransitionValues = (country) => {
    const countryIndex = countries.findIndex((d) => d === country)
    return { y: countryIndex * rowHeight }
  }

  const totalTransitions = countries.length
  let numberTransitionsCompleted = 0
  const transitions = useTransition(countries, (d) => d.Country, {
    enter: getTransitionValues,
    update: getTransitionValues,
    onRest: () => {
      numberTransitionsCompleted++
      if (numberTransitionsCompleted === totalTransitions) {
        if (scrollToCountryNeeded.current) {
          scrollToCountryNeeded.current = false
          if (
            selectedCountry !== "" &&
            selectedCountryRef.current &&
            props.containerRef
          ) {
            const scrollOffset =
              selectedCountryRef.current.getBoundingClientRect().y +
              props.containerRef.scrollTop -
              220 // 220 is about the size of the combined headers
            props.containerRef.scrollTo({
              top: scrollOffset,
              behavior: "smooth",
            })
          }
        }
      }
    },
  })

  const countryGroups = transitions.map((transition) => {
    if (transition.state === "leave") {
      return null
    }
    const country = transition.item
    const countryIndex = countries.findIndex((d) => d === country)
    const { y } = transition.props
    const textY = barHeight * 0.75
    const bgRect =
      countryIndex % 2 === 0 ? (
        <rect
          fill="#f2f2f2"
          width={labelWidth + fullBarWidth - sidePaddingToUse * 2}
          x={sidePaddingToUse}
          height={rowHeight}
        />
      ) : null
    const topMargins = (rowHeight - barHeight) / 2

    const selected = country.Country === selectedCountry
    const countryLabelWeight = selected ? "900" : null
    const countryOpacity = selectedCountry !== "" ? (selected ? 1 : 0.8) : 1
    const countryTextStyle = {
      fontWeight: countryLabelWeight,
      cursor: "pointer",
    }
    const factorBoxes = allFactors.map((factor, factorIndex) => {
      let rect = null
      let label = null
      const rankLabel = inSmallWidthMode
        ? `${country.sortedRank + 1}`
        : `${country.sortedRank + 1} / ${countries.length}`
      if (factorIndex !== 0) {
        const factorKey =
          factor !== "overall" ? `Factor ${factorIndex}: ${factor}` : "overall"
        const score = country[factorKey]
        const fill = gradient(score)
        rect = (
          <rect
            width={factorWidth - boxPadding * 2}
            height={barHeight + topMargins}
            y={-topMargins * 0.5}
            x={boxPadding}
            fill={fill}
          />
        )
        label = (
          <text
            x={factorWidth / 2}
            style={{ textAnchor: "middle", fill: "#fff" }}
            y={textY - 0.5}
          >
            {score.toFixed(2)}
          </text>
        )
      } else {
        label = (
          <text
            x={factorWidth / 2}
            style={{
              textAnchor: "middle",
              fontWeight: selected ? "700" : null,
            }}
            y={textY}
          >
            {rankLabel}
          </text>
        )
      }
      const x = factorIndex * factorWidth + labelWidth
      return (
        <g transform={`translate(${x}, 0)`} key={factor} className="tableCell">
          {rect}
          {label}
        </g>
      )
    })
    return (
      <animated.g
        key={country.Country}
        transform={y.interpolate((y) => `translate(0, ${y})`)}
        opacity={countryOpacity}
        ref={country.Country === selectedCountry ? selectedCountryRef : null}
      >
        {bgRect}
        <g transform={`translate(0, ${topMargins})`}>
          <text
            className="textLabel both"
            onClick={() => props.updateUrl("country")(country.Country)}
            x={labelWidth - labelRankPadding}
            y={textY}
            style={countryTextStyle}
          >
            {inSmallWidthMode
              ? getShortCountryLabel(country.Country)
              : country.Country}
          </text>
          {factorBoxes}
        </g>
      </animated.g>
    )
  })

  const headerLabelsOnOneLine = window.innerWidth < 460

  const tableHeaderHeight = inSmallWidthMode
    ? headerLabelsOnOneLine
      ? 110
      : 70
    : 36
  const headerTop = inSmallWidthMode
    ? document.querySelector(".Nav").getBoundingClientRect().height
    : null
  const primary = "#482D8B"
  const selectFactor = (factor, factorIndex) => {
    return () => {
      let factorToSet =
        factorIndex === 0 || factorIndex === allFactors.length - 1 ? "" : factor
      props.updateUrl("factor")(factorToSet)
    }
  }
  const headers = allFactors.map((factor, factorIndex) => {
    const x = labelWidth + factorIndex * factorWidth
    const selected = factorSelected
      ? factor === selectedFactor
      : factorIndex === 0 || factorIndex === allFactors.length - 1
    const textFill = selected ? "#fff" : primary
    const backgroundColor = selected ? primary : "#fff"
    let labelValues = factorHeaderLabels[factorIndex]
    if (factorIndex === 0) {
      if (selectedFilterIncome !== "") {
        labelValues = ["INCOME", "RANK"]
      }
      if (selectedFilterRegion !== "") {
        labelValues = ["REGIONAL", "RANK"]
      }
    }
    const singleRowFactorLabels = [labelValues.join(" ")]
    const labelsToUse = headerLabelsOnOneLine
      ? singleRowFactorLabels
      : labelValues
    const labels = labelsToUse.map((label, labelIndex) => {
      let y = 2 + 14 * (labelIndex + 1)
      let x = factorWidth / 2
      if (inSmallWidthMode && !headerLabelsOnOneLine) {
        if (labelIndex === 1) {
          y -= 14
          x += 14
        }
      }
      let rotate = ""
      let fontSize = 12
      if (window.innerWidth < 1350) {
        fontSize = 10
      }
      if (inSmallWidthMode) {
        rotate = `rotate(-90) translate(${
          headerLabelsOnOneLine
            ? `-${3.9 * fontSize}, ${0.2 * fontSize}`
            : `-${2 * fontSize}, -${0.5 * fontSize}`
        })`
      } else if (window.innerWidth < 1350) {
        rotate = `translate(0, -${0.2 * fontSize})`
      }
      const transform = `translate(${x}, ${y}) ${rotate}`
      return (
        <text
          key={labelIndex}
          style={{ fill: textFill }}
          transform={transform}
          className="headerLabel"
        >
          {label}
        </text>
      )
    })
    return (
      <g
        key={factor}
        transform={`translate(${x}, 0)`}
        onClick={selectFactor(factor, factorIndex)}
        className="factorHeader"
      >
        <rect
          fill={backgroundColor}
          width={factorWidth}
          height={tableHeaderHeight - 1}
          stroke={primary}
        />
        {labels}
      </g>
    )
  })
  return (
    <div className="globalInsightsTable">
      <div className="sticky" style={{ top: headerTop }}>
        <svg height={tableHeaderHeight} width={svgWidth} className="headers">
          {headers}
        </svg>
      </div>
      <svg height={svgHeight} width={svgWidth}>
        {countryGroups}
      </svg>
    </div>
  )
}
